'use client';
// BazaarReach — "الوصول الخارجي" card for the founder's x402 Payments
// console (v2.19.0). Answers the founder's exact question:
//   "كيف أبحث عن خدماتنا في مكان آخر خارج المنصة حتى نتأكد هل توجد
//    خدماتنا في x402 أم أنها تظهر فقط في منصتنا؟"
//
//   1. LIVE INDEX STATUS — what Coinbase's public Bazaar catalog knows
//      about our payTo address RIGHT NOW (merchant query + rank probes).
//   2. SELF-REGISTRATION — one click runs a real self-pay seeding batch
//      (vault→vault, net zero) so per-service resources enter the PUBLIC
//      catalog per the official spec (declaration + echo + settlement).
//   3. VERIFY-IT-YOURSELF — clickable EXTERNAL URLs the founder can open
//      from anywhere in the world: CDP discovery REST, x402scan.com,
//      402index.io, agentic.market, Google.
import { useState, useEffect } from 'react';
import { Globe, ExternalLink, Rocket, ShieldCheck, SearchCheck, CircleAlert, CheckCircle2, Hourglass, Loader2 } from 'lucide-react';
import { Mono, timeAgo, post } from './shared';

export interface BazaarIndexStatusView {
  checkedAt: number;
  indexedCount: number;
  indexedResources: Array<{ resource: string; lastUpdated: string | null; priceMicros: string | null }>;
  searchProbes: Array<{ query: string; rank: number | null; total: number | null }>;
  ok: boolean;
  detail: string;
}
export interface SeedResultView {
  serviceId: string; ok: boolean; paid: boolean;
  bazaar: string | null; rejectedReason: string | null;
  transaction: string | null; amountMicros: string | null; error: string | null; ms: number;
}
export interface SeedRunView {
  startedAt: number; finishedAt: number; target: string;
  results: SeedResultView[]; summary: string;
}

const BADGE: Record<string, { label: string; cls: string; icon: typeof CheckCircle2 }> = {
  success:    { label: 'مُفهرسة في البازار', cls: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/30', icon: CheckCircle2 },
  processing: { label: 'الفهرسة قيد المعالجة', cls: 'bg-amber-500/10 text-amber-400 ring-amber-500/30', icon: Hourglass },
  rejected:   { label: 'رُفضت الفهرسة', cls: 'bg-rose-500/10 text-rose-400 ring-rose-500/30', icon: CircleAlert },
};

export function BazaarReach({ bazaar, seedRuns, walletId, password, settlementAddress }: {
  bazaar: BazaarIndexStatusView | null;
  seedRuns: SeedRunView[];
  walletId: string;
  password: string;
  settlementAddress: string;
}) {
  const [running, setRunning] = useState(false);
  const [batchErr, setBatchErr] = useState<string | null>(null);
  const [localRuns, setLocalRuns] = useState<SeedRunView[]>(seedRuns);
  const [origin, setOrigin] = useState('');
  useEffect(() => { setOrigin(window.location.origin); }, []);

  const indexed = bazaar?.indexedCount ?? null;
  const lastRun = localRuns[0] ?? null;

  const pollStatus = async (): Promise<boolean> => {
    const r = await post<{ ok: boolean; running: boolean; history: SeedRunView[] }>('/api/admin/x402/bazaar-seed', { walletId, password, action: 'status' });
    setLocalRuns(r.history ?? []);
    return r.running;
  };

  const seed = async (opts: { batch?: number; mode?: 'featured' | 'remaining' } = {}) => {
    setRunning(true); setBatchErr(null);
    try {
      const r = await post<{ ok: boolean; started: boolean; note: string; history: SeedRunView[] }>('/api/admin/x402/bazaar-seed', { walletId, password, ...opts });
      setLocalRuns(r.history ?? []);
      if (!r.started) { setBatchErr(r.note); }
      // poll while the background batch is in flight (45 services ≈ minutes)
      let busy = true;
      let ticks = 0;
      while (busy && ticks < 120) {
        await new Promise(res => setTimeout(res, 5000));
        try { busy = await pollStatus(); } catch { busy = false; }
        ticks++;
      }
    } catch (e) {
      setBatchErr(e instanceof Error ? e.message : 'فشل تسجيل البازار');
    } finally { setRunning(false); }
  };

  const verifyLinks: Array<{ label: string; href: string; note: string }> = [
    {
      label: 'فهرس CDP — كتالوج عنوان التسوية',
      href: `https://api.cdp.coinbase.com/platform/v2/x402/discovery/merchant?payTo=${settlementAddress}`,
      note: 'أهم رابط: ما يسجّله هذا الرابط هو ما يراه كل وكلاء البازار. قبل التسجيل: total: 0',
    },
    {
      label: 'فهرس CDP — البحث الدلالي',
      href: 'https://api.cdp.coinbase.com/platform/v2/x402/discovery/search?query=weather%20api&limit=10',
      note: 'ابحث "weather api" أو "crypto price" — خدماتنا يجب أن تظهر في النتائج بعد التسجيل',
    },
    { label: 'x402scan.com — دليل البازار العام', href: 'https://www.x402scan.com/', note: 'واجهة بشرية لنفس الفهرس — ابحث عن amanchain' },
    { label: '402index.io — فهرس الموارد', href: 'https://402index.io/', note: 'فهرس مستقل يجمع موارد x402 المسوّاة فعلياً' },
    { label: 'agentic.market — سوق الوكلاء', href: 'https://agentic.market/', note: 'قرّاء آخرون لنفس كتالوج التسويات' },
    {
      label: 'Google — أثرنا في ويب الوكلاء',
      href: `https://www.google.com/search?q=${encodeURIComponent('"amanchain" x402 services')}`,
      note: 'بحث خارجي كلياً: من يذكرنا؟ (بعد التسجيل ستظهر روابطنا في الفهارس تدريجياً)',
    },
  ];

  return (
    <div className="glass p-5 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-bold flex items-center gap-2">
          <Globe className="size-4 text-sky-400" />
          الوصول الخارجي — هل تظهر خدماتنا خارج المنصة؟
        </div>
        {indexed === null ? (
          <span className="rounded-full bg-muted px-2.5 py-1 text-[0.62rem] font-bold text-muted-foreground">يفحص الفهرس…</span>
        ) : indexed > 0 ? (
          <span className="rounded-full bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/30 px-2.5 py-1 text-[0.62rem] font-black">
            {indexed} مورد مفهرس في البازار العام
          </span>
        ) : (
          <span className="rounded-full bg-rose-500/10 text-rose-400 ring-1 ring-rose-500/30 px-2.5 py-1 text-[0.62rem] font-black">
            صفر موارد خارجياً — خدماتنا داخل منصتنا فقط
          </span>
        )}
      </div>

      {/* the honest diagnosis */}
      <div className={`rounded-xl p-3 text-[0.7rem] leading-relaxed ring-1 ${indexed && indexed > 0 ? 'bg-emerald-500/5 text-emerald-200 ring-emerald-500/20' : 'bg-amber-500/5 text-amber-200 ring-amber-500/25'}`}>
        {indexed && indexed > 0 ? (
          <>
            <b>أخبار حقيقية:</b> مواردنا بدأت تظهر في الكتالوج العام أدناه. سجّل البقية حتى يكتمل حضور الـ45 خدمة، وتحقق بنفسك من الروابط الخارجية أسفل البطاقة.
            <div className="mt-2 space-y-1">
              {bazaar!.indexedResources.slice(0, 6).map(r => (
                <div key={r.resource} className="flex items-center gap-1.5 flex-wrap">
                  <CheckCircle2 className="size-3 text-emerald-400 shrink-0" />
                  <Mono className="text-[0.6rem]">{r.resource}</Mono>
                  {r.priceMicros && <span className="text-[0.58rem] text-muted-foreground">${(Number(r.priceMicros) / 1e6).toFixed(4)}</span>}
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <b>الحقيقة اللي لاحظتها صح 100%:</b> الوكلاء يصلون إلى وثائق الاكتشاف في منصتنا (وهذا ما يظهر في البث الحي)، لكن <b>الكتالوج الخارجي العائم — فهرس Coinbase Bazaar الذي تقرأه عشرات آلاف الوكلاء عبر x402scan.com و402index.io وBazaar MCP وAmazon Bedrock — لا يعرفنا إطلاقاً</b>.
            <br />
            <b>لماذا؟</b> القاعدة الرسمية للفهرسة: لا تُسجَّل أي خدمة إلا عندما تمر <b>تسوية دفع حقيقية</b> عبر المُيسّر ويُصدِر العميل الدافع امتداد <Mono className="text-[0.6rem]">bazaar</Mono>. معلنتنا وحدها لا تكفي — وتسوياتنا الأولية كانت قبل امتداد البازار.
            <br />
            <b>الحل الآن (زر التسجيل الذاتي):</b> المنصة تدفع لخدماتها بنفسها عبر مُيسّر كوين باس — الخزنة تدفع للخزنة، <b>صافي الحركة صفر دولار</b> وكوين باس تدفع الغاز — فتُسوّى كل خدمة تسوية حقيقية تدخل بها الكتالوج العام رسمياً.
          </>
        )}
      </div>

      {/* seed controls */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => void seed({ mode: 'remaining' })}
          disabled={running || !walletId}
          className="inline-flex items-center gap-1.5 rounded-xl bg-primary/15 text-primary ring-1 ring-primary/30 px-3 py-2 text-xs font-black hover:bg-primary/25 disabled:opacity-50"
        >
          {running ? <Loader2 className="size-3.5 animate-spin" /> : <Rocket className="size-3.5" />}
          {running ? 'يُسجّل الآن في الخلفية…' : indexed !== null && indexed < 45 ? `أكمل المتبقي فقط (${45 - indexed} خدمة)` : 'أكمل المتبقي فقط'}
        </button>
        <button
          onClick={() => void seed({ batch: 45 })}
          disabled={running || !walletId}
          className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/30 px-3 py-2 text-xs font-black hover:bg-emerald-500/20 disabled:opacity-50"
        >
          {running ? <Loader2 className="size-3.5 animate-spin" /> : <Rocket className="size-3.5" />}
          سجّل كل الـ45 خدمة
        </button>
        <span className="text-[0.6rem] text-muted-foreground flex items-center gap-1">
          <ShieldCheck className="size-3" /> دفع ذاتي آمن: خزنة ← باذر ← خزنة — كل ميكرو-USDC يعود للخزنة
        </span>
      </div>
      {indexed !== null && indexed < 45 && (
        <div className="rounded-xl bg-amber-500/5 text-amber-200 ring-1 ring-amber-500/25 p-2.5 text-[0.66rem] leading-relaxed">
          <b>خطة إكمال الـ{45 - indexed} الباقية:</b> أسعارها الأعلى من رصيد الخزنة الحالي، والدفع الذاتي دوران صفري — يكفي أن تحتوي الخزنة على سعر <b>أغلى خدمة واحدة</b> فقط لأن كل ميكرو يعود فوراً مع كل تسوية. اشحن خزنة التسوية بعنوان <Mono className="text-[0.6rem]" dir="ltr">{settlementAddress}</Mono> بمبلغ ≈0.35 USDC على شبكة Base ثم اضغط «أكمل المتبقي فقط». الخدمات الأرخص من الرصيد الحالي تُسجّل فوراً دون أي شحن.
        </div>
      )}
      {batchErr && <div className="rounded-xl bg-rose-500/10 text-rose-400 ring-1 ring-rose-500/30 p-2.5 text-xs font-bold">{batchErr}</div>}

      {/* last run results */}
      {lastRun && (
        <div className="rounded-xl bg-muted/40 ring-1 ring-border/60 p-3 space-y-2">
          <div className="text-[0.7rem] font-bold flex items-center justify-between flex-wrap gap-1">
            <span>آخر دفعة تسجيل — {timeAgo(lastRun.finishedAt)}</span>
            <span className="text-[0.62rem] font-normal text-muted-foreground">{lastRun.summary}</span>
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {lastRun.results.map(r => {
              const badge = r.bazaar ? BADGE[r.bazaar] : null;
              return (
                <div key={r.serviceId} className="flex items-center justify-between gap-2 rounded-lg bg-background/60 px-2.5 py-1.5">
                  <div className="min-w-0">
                    <div className="text-[0.68rem] font-bold flex items-center gap-1.5 flex-wrap">
                      <Mono className="text-[0.62rem]">{r.serviceId}</Mono>
                      {badge && <badge.icon className={`size-3 ${badge.cls.split(' ')[1]}`} />}
                      {badge && <span className={`rounded-full px-2 py-0.5 text-[0.56rem] font-black ring-1 ${badge.cls}`}>{badge.label}</span>}
                    </div>
                    {r.error && <div className="text-[0.58rem] text-rose-400 truncate">{r.error}</div>}
                    {r.rejectedReason && <div className="text-[0.58rem] text-amber-400">سبب الرفض: {r.rejectedReason}</div>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {r.transaction && (
                      <a href={`https://basescan.org/tx/${r.transaction}`} target="_blank" rel="noreferrer" className="rounded-lg bg-sky-500/10 text-sky-400 ring-1 ring-sky-500/30 px-2 py-0.5 text-[0.58rem] font-black">Basescan</a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* rank probes */}
      {bazaar && bazaar.searchProbes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {bazaar.searchProbes.map(p => (
            <span key={p.query} className={`rounded-full px-2.5 py-1 text-[0.6rem] font-bold ring-1 ${p.rank ? 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/30' : 'bg-muted/60 text-muted-foreground ring-border/60'}`}>
              «{p.query}» {p.rank ? `مرتبة #${p.rank} من ${p.total}` : 'غير موجودة في أول صفحة'}
            </span>
          ))}
        </div>
      )}

      {/* verify-it-yourself checklist */}
      <div className="rounded-xl bg-muted/30 ring-1 ring-border/60 p-3 space-y-2">
        <div className="text-[0.72rem] font-black flex items-center gap-1.5"><SearchCheck className="size-3.5 text-sky-400" /> تحقق بنفسك من خارج المنصة — افتح هذه الروابط من أي مكان في العالم:</div>
        {verifyLinks.map(l => (
          <a key={l.href} href={l.href} target="_blank" rel="noreferrer" className="block rounded-lg bg-background/70 px-2.5 py-1.5 hover:ring-1 hover:ring-primary/40">
            <div className="text-[0.66rem] font-bold flex items-center gap-1.5">
              <ExternalLink className="size-3 text-sky-400 shrink-0" />
              <span className="truncate" dir="ltr">{l.label}</span>
            </div>
            <div className="text-[0.58rem] text-muted-foreground">{l.note}</div>
          </a>
        ))}
        <div className="text-[0.58rem] text-muted-foreground">
          موقعنا العام للفهرسة: <Mono className="text-[0.6rem]" dir="ltr">{origin || '…'}</Mono> — كل مواردنا تبدأ بهذا العنوان وتنتهي بـ <Mono className="text-[0.6rem]" dir="ltr">/api/x402/&lt;serviceId&gt;</Mono>
        </div>
      </div>
    </div>
  );
}
